import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Heartbeat,
  FirstAid,
  Users,
  Pill,
  ArrowLeft,
  Clock,
  CaretRight,
  MagnifyingGlass,
  House,
  List,
  Check,
  Lightbulb,
  X
} from '@phosphor-icons/react';
import { educationCategories, educationTopics } from '../data/educationContent';

const iconMap = {
  BookOpen,
  Heartbeat,
  FirstAid,
  Users,
  Pill
};

export default function Education() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // If viewing a specific topic
  if (topicId && educationTopics[topicId]) {
    return <TopicView topic={educationTopics[topicId]} />;
  }

  // Filter topics based on search
  const filteredTopics = Object.values(educationTopics).filter(topic =>
    topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    topic.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-[#5B21B6]">MenoPath</Link>
              <span className="hidden sm:inline-flex items-center px-3 py-1 bg-[#EDE9FE] text-[#5B21B6] text-sm font-medium rounded-full">
                <BookOpen className="w-4 h-4 mr-1" /> Education Center
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="text-sm text-[#5B21B6] hover:underline hidden sm:block">
                Dashboard
              </Link>
              <Link to="/login" className="text-sm text-[#64748B] hover:text-[#5B21B6]">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#5B21B6] via-[#7C3AED] to-[#EC4899] py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Menopause Education Center
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-white/80 mb-8 max-w-2xl mx-auto"
          >
            Evidence-based information to help you understand and navigate your menopause journey with confidence.
          </motion.p>
          
          {/* Search Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative max-w-xl mx-auto"
          >
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <input
              type="text"
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border-0 shadow-lg focus:ring-2 focus:ring-white/50"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {searchQuery ? (
          // Search Results
          <div>
            <h2 className="text-xl font-semibold text-[#0F172A] mb-6">
              Search Results ({filteredTopics.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTopics.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>
            {filteredTopics.length === 0 && (
              <div className="text-center py-12">
                <MagnifyingGlass className="w-12 h-12 text-[#CBD5E1] mx-auto mb-4" />
                <p className="text-[#64748B]">No topics found matching "{searchQuery}"</p>
              </div>
            )}
          </div>
        ) : (
          // Categories View
          <div className="space-y-12">
            {educationCategories.map((category, idx) => {
              const IconComponent = iconMap[category.icon];
              const categoryTopics = category.topics.map(id => educationTopics[id]).filter(Boolean);
              
              return (
                <motion.section 
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color: category.color }} weight="duotone" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[#0F172A]">{category.title}</h2>
                      <p className="text-sm text-[#64748B]">{categoryTopics.length} topics</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categoryTopics.map((topic) => (
                      <TopicCard key={topic.id} topic={topic} color={category.color} />
                    ))}
                  </div>
                </motion.section>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E2E8F0] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-[#64748B] mb-4">
            This educational content is for informational purposes only and does not constitute medical advice.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <Link to="/" className="text-[#5B21B6] hover:underline">Home</Link>
            <Link to="/about" className="text-[#5B21B6] hover:underline">About</Link>
            <Link to="/faq" className="text-[#5B21B6] hover:underline">FAQ</Link>
            <Link to="/contact" className="text-[#5B21B6] hover:underline">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Topic Card Component
const TopicCard = ({ topic, color = '#5B21B6' }) => {
  const category = educationCategories.find(c => c.id === topic.category);
  const cardColor = category?.color || color;
  
  return (
    <Link to={`/education/${topic.id}`}>
      <motion.div 
        whileHover={{ y: -4 }}
        className="bg-white rounded-xl border border-[#E2E8F0] p-6 h-full hover:shadow-lg transition-all hover:border-[#5B21B6]/30"
      >
        <div className="flex items-center gap-2 text-xs text-[#64748B] mb-3">
          <Clock className="w-4 h-4" />
          {topic.readTime}
        </div>
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{topic.title}</h3>
        <p className="text-sm text-[#64748B] mb-4">{topic.subtitle}</p>
        <div className="flex items-center text-sm font-medium" style={{ color: cardColor }}>
          Read More <CaretRight className="w-4 h-4 ml-1" />
        </div>
      </motion.div>
    </Link>
  );
};

// Topic View Component (Full Article)
const TopicView = ({ topic }) => {
  const navigate = useNavigate();
  const category = educationCategories.find(c => c.id === topic.category);
  const [activeSection, setActiveSection] = useState(0);
  
  // Get related topics from same category
  const relatedTopics = category?.topics
    .filter(id => id !== topic.id)
    .map(id => educationTopics[id])
    .filter(Boolean)
    .slice(0, 2) || [];

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/education')}
                className="flex items-center gap-2 text-[#64748B] hover:text-[#5B21B6]"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Education</span>
              </button>
            </div>
            <Link to="/" className="text-xl font-bold text-[#5B21B6]">MenoPath</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-[#64748B] mb-6">
          <Link to="/education" className="hover:text-[#5B21B6]">Education</Link>
          <CaretRight className="w-4 h-4" />
          <span className="text-[#0F172A]">{topic.title}</span>
        </nav>

        {/* Article Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-sm font-medium"
              style={{ 
                backgroundColor: `${category?.color}15`,
                color: category?.color
              }}
            >
              {category?.title}
            </span>
            <span className="flex items-center gap-1 text-sm text-[#64748B]">
              <Clock className="w-4 h-4" />
              {topic.readTime}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-3">
            {topic.title}
          </h1>
          <p className="text-lg text-[#64748B]">{topic.subtitle}</p>
        </motion.div>

        {/* Article Content */}
        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] overflow-hidden"
        >
          {/* Introduction */}
          <div className="p-6 sm:p-8 border-b border-[#E2E8F0] bg-gradient-to-br from-[#F8F6FF] to-white">
            <p className="text-lg text-[#334155] leading-relaxed">
              {topic.content.intro}
            </p>
          </div>

          {/* Table of Contents */}
          <div className="p-6 sm:p-8 border-b border-[#E2E8F0] bg-[#F8FAFC]">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-[#5B21B6]" />
              <h2 className="font-semibold text-[#0F172A]">In This Article</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {topic.content.sections.map((section, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveSection(idx);
                    document.getElementById(`section-${idx}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-left px-3 py-2 rounded-lg text-sm text-[#64748B] hover:bg-[#EDE9FE] hover:text-[#5B21B6] transition-colors"
                >
                  {idx + 1}. {section.title}
                </button>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="p-6 sm:p-8 space-y-8">
            {topic.content.sections.map((section, idx) => (
              <section key={idx} id={`section-${idx}`} className="scroll-mt-20">
                <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                  <span 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                    style={{ backgroundColor: category?.color }}
                  >
                    {idx + 1}
                  </span>
                  {section.title}
                </h2>
                <div className="prose prose-slate max-w-none">
                  {section.content.split('\n\n').map((paragraph, pIdx) => (
                    <div key={pIdx} className="mb-4">
                      {paragraph.startsWith('**') && paragraph.includes(':**') ? (
                        // Bold header with content
                        <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                          <p className="font-semibold text-[#0F172A] mb-2">
                            {paragraph.split(':**')[0].replace(/\*\*/g, '')}
                          </p>
                          <p className="text-[#64748B] text-sm whitespace-pre-line">
                            {paragraph.split(':**')[1]}
                          </p>
                        </div>
                      ) : paragraph.startsWith('•') || paragraph.includes('\n•') ? (
                        // Bullet list
                        <ul className="space-y-2">
                          {paragraph.split('\n').filter(line => line.trim()).map((line, lIdx) => (
                            <li key={lIdx} className="flex items-start gap-2 text-[#334155]">
                              <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                              <span>{line.replace(/^[•\-]\s*/, '')}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        // Regular paragraph
                        <p className="text-[#334155] leading-relaxed whitespace-pre-line">
                          {paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .split(/<strong>|<\/strong>/)
                            .map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Key Takeaways */}
          <div className="p-6 sm:p-8 bg-gradient-to-br from-[#5B21B6]/5 to-[#EC4899]/5 border-t border-[#E2E8F0]">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-[#F59E0B]" weight="duotone" />
              <h2 className="font-bold text-[#0F172A]">Key Takeaways</h2>
            </div>
            <ul className="space-y-3">
              {topic.content.keyTakeaways.map((takeaway, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: category?.color }}
                  >
                    <Check className="w-4 h-4 text-white" weight="bold" />
                  </div>
                  <span className="text-[#334155]">{takeaway}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.article>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-[#FEF3C7] rounded-xl border border-[#FDE68A]">
          <p className="text-sm text-[#92400E]">
            <strong>Medical Disclaimer:</strong> This content is for educational purposes only and is not intended as medical advice. 
            Always consult with a qualified healthcare provider for personalized medical guidance.
          </p>
        </div>

        {/* Related Topics */}
        {relatedTopics.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-[#0F172A] mb-6">Related Topics</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {relatedTopics.map((related) => (
                <TopicCard key={related.id} topic={related} color={category?.color} />
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-12 flex items-center justify-between">
          <Link 
            to="/education"
            className="flex items-center gap-2 text-[#5B21B6] hover:underline"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to All Topics
          </Link>
          <Link 
            to="/dashboard"
            className="flex items-center gap-2 px-4 py-2 bg-[#5B21B6] text-white rounded-lg hover:bg-[#4C1D95]"
          >
            Go to Dashboard
            <CaretRight className="w-5 h-5" />
          </Link>
        </div>
      </main>
    </div>
  );
};
